import { ReactComponent as AllProductsIcon } from "@assets/icons/sidebar/ic_small_bag.svg";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Collapse } from "@mui/material";
import { selectOperation } from "@redux/features/dfs/dfsSlice";
import { performImport } from "@redux/features/dfs/thunks";
import { isDataTransferStateActive } from "@redux/features/dfs/types";
import { useImportSusoftDataMutation } from "@redux/features/integrations/susoftApi";
import { useGetModuleLocationsQuery } from "@redux/features/locations/locationsApi";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import BaseButton from "@reusables/BaseButton";
import BaseDropdown from "@reusables/dropdowns/BaseDropdown";
import BaseModal from "@reusables/Modals/BaseModal";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Integration } from "@/types/general";
import { getIntegrationImportHistory, isReimportConfirmationError } from "../shared/importHistory";
import { useReimportGuard } from "../shared/useReimportGuard";
import ReimportConfirmation from "../shared/ReimportConfirmation";
import ImportEntitySummaryCard from "../shared/ImportEntitySummaryCard";

interface SusoftImportModalProps {
    integration: Integration.Components.Integration;
    isOpen: boolean;
    onClose: () => void;
}

const formSchema = z.object({
    location: z.object({
        store: z.object({
            id: z.number(),
            name: z.string(),
        }),
        section: z
            .object({
                id: z.number(),
                name: z.string(),
            })
            .nullish(),
    }),
});

type FormTyping = z.infer<typeof formSchema>;

const importPlan: Integration.Import.Plan = {
    integration: "susoft",
    entities: ["product"],
};

export default function SusoftImportModal({
    integration,
    isOpen,
    onClose,
}: SusoftImportModalProps) {
    const { t } = useTranslation("", {
        keyPrefix: "imports.modules.susoft.importModal",
    });

    const dispatch = useAppDispatch();

    const [importSusoft, { isLoading: isImportLoading }] = useImportSusoftDataMutation();

    const isImportInProgress = useAppSelector((state) =>
        isDataTransferStateActive(selectOperation("import")(state))
    );

    const { data: locationsOptions, isLoading: isLocationsOptionsLoading } =
        useGetModuleLocationsQuery("integrations");

    const form = useForm<FormTyping>({
        resolver: zodResolver(formSchema),
    });

    const historyByEntity = getIntegrationImportHistory(integration);
    const reimportGuard = useReimportGuard({ historyByEntity });

    const handleClose = () => {
        reimportGuard.cancelConfirmation();
        onClose();
    };

    const onSubmit = form.handleSubmit((data) => {
        void reimportGuard.requestImport(
            importPlan,
            ({ reimportConfirmed }) =>
                dispatch(
                    performImport(
                        "susoft",
                        () => {
                            return importSusoft({
                                entities: ["product"],
                                default_product_location: {
                                    store_id: data.location.store.id,
                                    section_id: data.location.section?.id,
                                },
                                ...(reimportConfirmed && { reimport_confirmed: true }),
                            }).unwrap();
                        },
                        { shouldSuppressErrorToast: isReimportConfirmationError }
                    )
                ),
            () => {
                form.reset();
                handleClose();
            }
        );
    });

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={handleClose}
            width={800}
            padding="45px"
            useCloseIcon
            isLoading={isImportLoading && !reimportGuard.pendingImport}
        >
            {reimportGuard.pendingImport ? (
                <ReimportConfirmation
                    integrationName={integration.name}
                    riskyEntities={reimportGuard.pendingImport.riskyEntities}
                    safeEntities={reimportGuard.pendingImport.safeEntities}
                    onBack={reimportGuard.cancelConfirmation}
                    onConfirm={() => void reimportGuard.confirmImport()}
                    isLoading={reimportGuard.isConfirmingImport}
                />
            ) : (
                <form onSubmit={onSubmit}>
                    <div className="text-xl font-semibold text-center text-accent mb-4">
                        {t("heading")}
                    </div>

                    <Collapse in={isImportInProgress}>
                        <Alert severity={"warning"} className={"mb-4"}>
                            {t("importIsAlreadyInProgressAlert")}
                        </Alert>
                    </Collapse>

                    {/* What we import */}
                    <div className={"space-y-2 mb-4"}>
                        <div className="font-semibold text-inputs-label-dim">
                            {t("whatWeImport.label")}
                        </div>
                        <div className={" text-accent"}>
                            <ImportEntitySummaryCard
                                label={t("whatWeImport.products")}
                                icon={<AllProductsIcon width={24} height={24} />}
                                history={historyByEntity.product}
                            />
                        </div>
                    </div>

                    <div>
                        <Controller
                            name={"location"}
                            control={form.control}
                            render={({ field, fieldState }) => (
                                <BaseDropdown
                                    {...field}
                                    error={fieldState.error}
                                    label={t("locationDropdown.label") + " *"}
                                    placeholder={t("locationDropdown.placeholder")}
                                    options={locationsOptions}
                                    getter={{
                                        label: (opt) =>
                                            `${opt.store.name}${
                                                opt.section ? ` - ${opt.section.name}` : ""
                                            }`,
                                        key: (opt) =>
                                            opt.section
                                                ? `${opt.store.id}_${opt.section.id}`
                                                : `${opt.store.id}`,
                                        renderOption: (opt, icon) => (
                                            <div>
                                                <div className="grow">
                                                    <span className="bold-highlight">
                                                        {opt.store.name}
                                                    </span>
                                                    {opt.section ? (
                                                        <> - {opt.section.name}</>
                                                    ) : null}
                                                </div>
                                                {icon}
                                            </div>
                                        ),
                                    }}
                                    isLoading={isLocationsOptionsLoading}
                                    autocomplete
                                    virtualize
                                />
                            )}
                        />
                    </div>

                    <div className={"mt-8 grid grid-cols-2 gap-2"}>
                        <BaseButton
                            buttonWidth="100%"
                            text={t("buttons.cancel")}
                            size="md"
                            type="button"
                            primaryOutlined
                            onClick={handleClose}
                        />
                        <BaseButton
                            buttonWidth="100%"
                            text={t("buttons.import")}
                            size="md"
                            type="submit"
                            loading={isImportInProgress}
                        />
                    </div>
                </form>
            )}
        </BaseModal>
    );
}
